/**
 * CVCanvasPreview.jsx
 * Shared canvas components for CV rendering (Classic / Modern / Minimal templates).
 *
 * Exports:
 *   CVCanvas      — A4 wrapper; pass canvasEdit=undefined for read-only (salt-görüntü)
 *   TEMPLATE_IDS  — ['classic', 'modern', 'minimal']
 *
 * All inner components are module-private.
 * canvasEdit = undefined → isEdit = false → salt-görüntü (no pencil, no hover overlay).
 */

import { useState, useEffect, useRef } from 'react';
import {
  AlertCircle, Eye, EyeOff, Camera, Loader2,
  Plus, Trash2, X, Pencil,
} from 'lucide-react';

// ── Private helper ────────────────────────────────────────────────────────────

function interpolateStr(str, vars) {
  if (!str) return str;
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? '')),
    str
  );
}

// ── InlineField ───────────────────────────────────────────────────────────────

/**
 * Inline editable field — read mode = span, edit mode = input/textarea.
 * variant='header-white': dark-bg variant (transparent input, white text).
 * inputClassName/readClassName: additional Tailwind classes to override appearance.
 * placeholder: shown in read mode when value is empty.
 */
function InlineField({
  fieldId, value, onSave, t, required, multiline, maxLength, ariaLabel,
  onEditStart, onEditEnd,
  variant, inputClassName, readClassName, placeholder,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(false);
  const inputRef = useRef(null);
  const readRef = useRef(null);
  const isHeaderWhite = variant === 'header-white';

  // Sync if parent value changes
  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const startEdit = () => {
    setDraft(value || '');
    setError(null);
    setEditing(true);
    if (onEditStart) onEditStart();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commit = () => {
    if (required && !draft.trim()) {
      setError(t('cv_wizard.edit.required'));
      return;
    }
    onSave(fieldId, draft.trim());
    setEditing(false);
    setError(null);
    if (onEditEnd) onEditEnd();
    setFlash(true);
    setTimeout(() => {
      setFlash(false);
      readRef.current?.focus();
    }, 700);
  };

  const cancel = () => {
    setDraft(value || '');
    setError(null);
    setEditing(false);
    if (onEditEnd) onEditEnd();
    setTimeout(() => readRef.current?.focus(), 50);
  };

  const handleKeyDown = (e) => {
    if (!multiline) {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') cancel();
    } else {
      if (e.key === 'Escape') cancel();
      // Shift+Enter → allow (default browser newline)
    }
  };

  const handleBlur = () => {
    if (multiline) commit();
  };

  const displayValue = value || '';
  const emptyLabel = placeholder || t('cv_wizard.edit.empty_field');

  if (editing) {
    const whiteInputCls = `bg-transparent border-b-2 border-white/60 text-white placeholder-white/40 focus:border-white focus:outline-none w-full rounded-none ${inputClassName || ''}`;
    const defaultInputCls = `w-full px-3 py-2 rounded-xl border-2 text-sm font-medium bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 ${error ? 'border-rose-400' : 'border-sky-400'} ${inputClassName || ''}`;
    return (
      <div className="flex flex-col gap-1 w-full">
        {multiline ? (
          <textarea
            ref={inputRef}
            id={fieldId}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            maxLength={maxLength}
            rows={4}
            aria-label={ariaLabel}
            aria-invalid={!!error}
            className={isHeaderWhite
              ? `bg-transparent border-b-2 border-white/60 text-white focus:border-white focus:outline-none w-full rounded-none resize-none ${inputClassName || ''}`
              : `w-full px-3 py-2 rounded-xl border-2 text-sm font-medium bg-white resize-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 ${error ? 'border-rose-400' : 'border-sky-400'} ${inputClassName || ''}`}
          />
        ) : (
          <input
            ref={inputRef}
            id={fieldId}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={commit}
            maxLength={maxLength}
            aria-label={ariaLabel}
            aria-invalid={!!error}
            className={isHeaderWhite ? whiteInputCls : defaultInputCls}
          />
        )}
        {maxLength && multiline && (
          <p className={`text-[11px] text-right font-medium ${draft.length > maxLength - 20 ? 'text-amber-600' : 'text-gray-500'}`}>
            {interpolateStr(t('cv_wizard.edit.hakkimda_counter'), { count: draft.length })}
          </p>
        )}
        {error && (
          <p role="alert" className="flex items-center gap-1 text-xs text-rose-600 font-medium">
            <AlertCircle size={12} aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    );
  }

  // Build read-mode className
  const readCls = isHeaderWhite
    ? [
        'group inline-flex items-center gap-1 rounded px-0.5 py-0.5 cursor-text transition-colors min-h-[22px]',
        flash ? 'bg-white/25 ring-2 ring-white/60' : 'hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60',
        !displayValue && 'opacity-50 italic',
        readClassName || '',
      ].filter(Boolean).join(' ')
    : [
        'group inline-flex items-center gap-1.5 rounded-lg px-1 py-0.5 cursor-pointer transition-colors min-h-[28px] text-sm',
        flash ? 'bg-emerald-50 text-emerald-800 ring-2 ring-emerald-300' : 'hover:bg-sky-50 hover:text-sky-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
        displayValue ? 'text-gray-800 font-medium' : 'text-gray-400 italic',
        readClassName || '',
      ].filter(Boolean).join(' ');

  return (
    <span
      ref={readRef}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={startEdit}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(); } }}
      className={readCls}
    >
      {displayValue || emptyLabel}
      <Pencil
        size={12}
        className={`flex-shrink-0 transition-opacity ${flash ? 'opacity-0' : 'opacity-0 group-hover:opacity-60 group-focus-visible:opacity-60'}`}
        aria-hidden="true"
      />
    </span>
  );
}

// ── createRenderF ─────────────────────────────────────────────────────────────

/**
 * createRenderF — factory that returns a renderF closure for a given canvasEdit context.
 * Each template calls this at the top of its body.
 */
function createRenderF(isEdit, canvasEdit, t, editCallbacks) {
  return function renderF(fieldId, value, opts = {}) {
    if (!isEdit) {
      if (!value) return null;
      return <span className={opts.readCls || ''}>{value}</span>;
    }
    return (
      <InlineField
        fieldId={fieldId}
        value={value || ''}
        onSave={canvasEdit.onFieldSave}
        t={t}
        required={opts.required}
        multiline={opts.multiline}
        maxLength={opts.maxLength}
        ariaLabel={opts.ariaLabel || fieldId}
        variant={opts.variant}
        inputClassName={opts.inputCls}
        readClassName={opts.readCls}
        placeholder={opts.placeholder}
        {...editCallbacks}
      />
    );
  };
}

// ── EditableExperienceList ────────────────────────────────────────────────────

/**
 * EditableExperienceList — shared iş deneyimi list for all three canvas templates.
 * cls: { entryWrapper, pozisyon, tarih, sirket, madde } Tailwind class overrides.
 */
function EditableExperienceList({ displayedDenWithIdx, canvasEdit, t, cls = {} }) {
  const isEdit = !!canvasEdit?.onFieldSave;
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};
  const renderF = createRenderF(isEdit, canvasEdit, t, editCallbacks);

  return (
    <div className="space-y-5">
      {displayedDenWithIdx.length === 0 && isEdit && (
        <p className="text-xs text-gray-400 italic">{t('cv_wizard.canvas.empty_deneyim')}</p>
      )}
      {displayedDenWithIdx.map(({ data: e, origIdx }) => (
        <div key={origIdx} className={`${isEdit ? 'relative group/den' : ''} ${cls.entryWrapper || ''}`}>
          {isEdit && (
            <button
              type="button"
              aria-label={t('cv.btn.deneyim_sil')}
              onClick={() => canvasEdit.onDeneyimDelete(origIdx)}
              className="absolute -right-5 top-0 opacity-0 group-hover/den:opacity-100 p-1 rounded text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-all focus-visible:opacity-100"
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          )}
          <div className="flex justify-between items-baseline gap-2 flex-wrap">
            <span className={cls.pozisyon || 'font-bold text-gray-800 text-sm'}>
              {isEdit ? renderF(`is_deneyimi.${origIdx}.pozisyon`, e.pozisyon || '', {
                ariaLabel: t('cv_wizard.edit.entry.pozisyon'),
                readCls: cls.pozisyon || 'font-bold text-gray-800 text-sm',
                placeholder: t('cv.placeholder.is_pozisyon'),
              }) : (e.pozisyon || '—')}
            </span>
            <span className={`text-xs whitespace-nowrap flex-shrink-0 flex gap-1 items-baseline ${cls.tarih || 'text-gray-500'}`}>
              {isEdit ? (
                <>
                  {renderF(`is_deneyimi.${origIdx}.baslangic`, e.baslangic || '', {
                    ariaLabel: t('cv.field.is_baslangic'),
                    readCls: `text-xs ${cls.tarih || 'text-gray-500'}`,
                    inputCls: 'text-xs w-20',
                    placeholder: t('cv.placeholder.is_baslangic'),
                  })}
                  <span aria-hidden="true">–</span>
                  {!e.devam_ediyor
                    ? renderF(`is_deneyimi.${origIdx}.bitis`, e.bitis || '', {
                        ariaLabel: t('cv.field.is_bitis'),
                        readCls: `text-xs ${cls.tarih || 'text-gray-500'}`,
                        inputCls: 'text-xs w-20',
                        placeholder: t('cv.placeholder.is_bitis'),
                      })
                    : <span>…</span>}
                </>
              ) : (
                [e.baslangic, e.devam_ediyor ? '…' : e.bitis].filter(Boolean).join(' – ')
              )}
            </span>
          </div>
          <div className={`text-xs mt-0.5 ${cls.sirket || 'text-gray-500'}`}>
            {isEdit ? renderF(`is_deneyimi.${origIdx}.sirket`, e.sirket || '', {
              ariaLabel: t('cv_wizard.edit.entry.sirket'),
              readCls: `text-xs ${cls.sirket || 'text-gray-500'}`,
              placeholder: t('cv.placeholder.is_sirket'),
            }) : (e.sirket || null)}
          </div>
          {(Array.isArray(e.maddeler) && (e.maddeler.length > 0 || isEdit)) && (
            <ul className="mt-2 space-y-1 pl-1">
              {(e.maddeler || []).map((m, mi) => (
                <li key={mi} className={`text-xs flex gap-1.5 items-start ${isEdit ? 'group/madde' : ''} ${cls.madde || 'text-gray-700'}`}>
                  <span className="text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true">•</span>
                  <span className="flex-1">
                    {isEdit ? renderF(`is_deneyimi.${origIdx}.maddeler.${mi}`, m || '', {
                      ariaLabel: t('cv_wizard.canvas.aria_madde'),
                      readCls: `text-xs ${cls.madde || 'text-gray-700'}`,
                      placeholder: t('cv.placeholder.is_madde'),
                    }) : m}
                  </span>
                  {isEdit && (
                    <button
                      type="button"
                      aria-label={t('cv.btn.madde_sil')}
                      onClick={() => canvasEdit.onDeneyimMaddeDelete(origIdx, mi)}
                      className="opacity-0 group-hover/madde:opacity-100 p-0.5 text-rose-400 hover:text-rose-600 transition-all flex-shrink-0 focus-visible:opacity-100"
                    >
                      <X size={11} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
              {isEdit && (e.maddeler || []).length < 4 && (
                <li>
                  <button
                    type="button"
                    onClick={() => canvasEdit.onDeneyimMaddeAdd(origIdx)}
                    className="text-xs text-sky-600 hover:text-sky-800 flex items-center gap-1 mt-0.5 focus-visible:underline"
                  >
                    <Plus size={10} aria-hidden="true" />
                    {t('cv.btn.madde_ekle')}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      ))}
      {isEdit && (
        <button
          type="button"
          onClick={canvasEdit.onDeneyimAdd}
          className="flex items-center gap-1.5 text-xs text-sky-700 border border-dashed border-sky-300 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors w-full justify-center focus-visible:outline-2 focus-visible:outline-sky-500"
        >
          <Plus size={12} aria-hidden="true" />
          {t('cv_wizard.canvas.add_deneyim')}
        </button>
      )}
    </div>
  );
}

// ── EditableDilList ───────────────────────────────────────────────────────────

/**
 * EditableDilList — shared diller list for all three canvas templates.
 * cls: { dilText, seviyeText } Tailwind class overrides.
 */
function EditableDilList({ displayedDilWithIdx, dilIngilizce, canvasEdit, t, cls = {} }) {
  const isEdit = !!canvasEdit?.onFieldSave;
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};
  const renderF = createRenderF(isEdit, canvasEdit, t, editCallbacks);

  return (
    <div className="space-y-1">
      {/* İngilizce satırı — edit modunda InlineField ile düzenlenebilir */}
      {(dilIngilizce || isEdit) && (
        <div className={`flex items-center gap-1 ${isEdit ? 'group/ing' : ''}`}>
          <span className={`text-sm ${cls.dilText || 'text-gray-800'} flex-shrink-0`}>{'İngilizce'}</span>
          {isEdit ? (
            <>
              <span className="text-gray-400 text-xs mx-0.5" aria-hidden="true">·</span>
              {renderF('canvas.dil.ingilizce_seviye', dilIngilizce || '', {
                ariaLabel: t('cv_wizard.edit.field.ing_seviye'),
                readCls: cls.seviyeText || 'text-xs text-gray-500',
                inputCls: 'text-xs w-24',
                placeholder: t('cv_wizard.canvas.placeholder_ing_seviye'),
              })}
            </>
          ) : (
            dilIngilizce && (
              <span className={`text-xs ${cls.seviyeText || 'text-gray-500'}`}>· {dilIngilizce}</span>
            )
          )}
        </div>
      )}
      {displayedDilWithIdx.length === 0 && isEdit && (
        <p className="text-xs text-gray-400 italic">{t('cv_wizard.canvas.empty_dil')}</p>
      )}
      {displayedDilWithIdx.map(({ data: d, origIdx }) => (
        <div key={origIdx} className={`flex items-center gap-2 ${isEdit ? 'group/dil' : ''}`}>
          <span className={`flex-1 flex items-baseline gap-1 flex-wrap ${cls.dilText || 'text-sm text-gray-800'}`}>
            {isEdit ? (
              <>
                {renderF(`dil.diller.${origIdx}.dil`, d.dil || '', {
                  ariaLabel: t('cv.field.dil_adi'),
                  readCls: cls.dilText || 'text-sm text-gray-800',
                  placeholder: t('cv_wizard.canvas.placeholder_dil_adi'),
                })}
                <span className="text-gray-400 text-xs" aria-hidden="true">·</span>
                {renderF(`dil.diller.${origIdx}.seviye`, d.seviye || '', {
                  ariaLabel: t('cv.field.dil_seviye'),
                  readCls: cls.seviyeText || 'text-gray-500 text-xs',
                  inputCls: 'text-xs w-20',
                  placeholder: t('cv_wizard.canvas.placeholder_dil_seviye'),
                })}
              </>
            ) : (
              <>
                {d.dil}
                {d.seviye && <span className={`text-xs ${cls.seviyeText || 'text-gray-500'}`}> · {d.seviye}</span>}
              </>
            )}
          </span>
          {isEdit && (
            <button
              type="button"
              aria-label={t('cv.btn.dil_sil')}
              onClick={() => canvasEdit.onDilDelete(origIdx)}
              className="opacity-0 group-hover/dil:opacity-100 p-1 text-rose-400 hover:text-rose-600 transition-all flex-shrink-0 focus-visible:opacity-100"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {isEdit && (
        <button
          type="button"
          onClick={canvasEdit.onDilAdd}
          className="flex items-center gap-1.5 text-xs text-sky-700 border border-dashed border-sky-300 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors w-full justify-center mt-1 focus-visible:outline-2 focus-visible:outline-sky-500"
        >
          <Plus size={12} aria-hidden="true" />
          {t('cv_wizard.canvas.add_dil')}
        </button>
      )}
    </div>
  );
}

// ── EditableSertifikalarList ──────────────────────────────────────────────────

function EditableSertifikalarList({ canvasSertifikalar = [], canvasEdit, t, cls = {} }) {
  const isEdit = !!canvasEdit?.onFieldSave;
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};
  const renderF = createRenderF(isEdit, canvasEdit, t, editCallbacks);
  const items = canvasSertifikalar.filter(Boolean);

  if (!isEdit && items.length === 0) return null;

  return (
    <div className="space-y-1">
      {isEdit && items.length === 0 && (
        <p className="text-xs text-gray-400 italic">{t('cv_wizard.canvas.empty_sertifikalar')}</p>
      )}
      {items.map((sertifika, idx) => (
        <div key={idx} className={`flex items-center gap-2 ${isEdit ? 'group/sert' : ''}`}>
          <span className={`flex-1 ${cls.text || 'text-xs text-gray-700'}`}>
            {isEdit ? (
              renderF(`egitim.sertifikalar.${idx}`, sertifika, {
                ariaLabel: t('cv_wizard.edit.field.sertifikalar'),
                readCls: cls.text || 'text-xs text-gray-700',
                placeholder: t('cv_wizard.canvas.placeholder_sertifika'),
              })
            ) : (
              sertifika
            )}
          </span>
          {isEdit && (
            <button
              type="button"
              aria-label={t('cv.btn.sil') || 'Sil'}
              onClick={() => canvasEdit.onSertifikaDelete(idx)}
              className="opacity-0 group-hover/sert:opacity-100 p-1 text-rose-400 hover:text-rose-600 transition-all flex-shrink-0 focus-visible:opacity-100"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {isEdit && (
        <button
          type="button"
          onClick={canvasEdit.onSertifikaAdd}
          className="flex items-center gap-1.5 text-xs text-sky-700 border border-dashed border-sky-300 rounded-lg px-3 py-2 hover:bg-sky-50 transition-colors w-full justify-center mt-1 focus-visible:outline-2 focus-visible:outline-sky-500"
        >
          <Plus size={12} aria-hidden="true" />
          {t('cv_wizard.canvas.add_sertifika')}
        </button>
      )}
    </div>
  );
}

// ── ClassicSectionHeading ─────────────────────────────────────────────────────

/** Section heading satırı — Klasik şablon için */
function ClassicSectionHeading({ label, isHidden, onToggle, t }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs font-black text-gray-600 uppercase tracking-widest leading-none">
        {label}
      </span>
      {onToggle ? (
        <button
          type="button"
          onClick={() => onToggle(isHidden)}
          className="flex-shrink-0 text-gray-500 hover:text-sky-600 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sky-600 rounded transition-colors"
          title={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-label={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-pressed={!isHidden}
        >
          {isHidden ? <EyeOff size={13} aria-hidden="true" /> : <Eye size={13} aria-hidden="true" />}
        </button>
      ) : (
        isHidden && <EyeOff size={12} className="text-gray-500 shrink-0" title="CV'de gizli" aria-label="CV'de gizli" />
      )}
      <hr className="flex-1 border-t-2 border-gray-100" aria-hidden="true" />
    </div>
  );
}

// ── CanvasFotoCircle ──────────────────────────────────────────────────────────

/**
 * CanvasFotoCircle — shared photo circle for ClassicTemplate and ModernTemplate.
 * In edit mode shows a file picker trigger (empty) or hover overlay with change/delete
 * buttons (when photo exists). Loading overlay is always-visible during upload/delete.
 */
function CanvasFotoCircle({
  fotoBlobUrl, isEdit, onFotoClick, onFotoDelete,
  uploading, deleting, t, sizeClass, ringOffsetColor, borderClass,
}) {
  const fileInputRef = useRef(null);
  const isLoading = uploading || deleting;
  const loadingLabel = uploading ? t('cv.btn.foto_uploading') : t('cv.btn.foto_deleting');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && onFotoClick) onFotoClick(file);
    // Reset so the same file can be re-selected if needed
    e.target.value = '';
  };

  const openPicker = () => {
    if (!isLoading) fileInputRef.current?.click();
  };

  // Read-only (non-edit) rendering
  if (!isEdit) {
    if (fotoBlobUrl) {
      return (
        <img
          src={fotoBlobUrl}
          alt={t('cv.foto.preview_alt')}
          className={`${sizeClass} rounded-full object-cover border-[3px] ${borderClass} flex-shrink-0`}
        />
      );
    }
    return (
      <div className={`${sizeClass} rounded-full border-[3px] border-dashed ${borderClass} flex items-center justify-center flex-shrink-0`}>
        <Camera size={28} className="text-white/40" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={`${sizeClass} rounded-full relative flex-shrink-0 group`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="sr-only"
        onChange={handleFileChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      {fotoBlobUrl ? (
        <img
          src={fotoBlobUrl}
          alt={t('cv.foto.preview_alt')}
          className={`${sizeClass} rounded-full object-cover border-[3px] ${borderClass}`}
        />
      ) : (
        /* Empty placeholder — clickable only in edit mode */
        <button
          type="button"
          onClick={openPicker}
          disabled={isLoading}
          aria-disabled={isLoading || undefined}
          aria-label={t('cv.btn.foto_yukle')}
          className={`${sizeClass} rounded-full border-[3px] border-dashed ${borderClass} flex items-center justify-center hover:border-white/70 hover:text-white/70 transition-all duration-150 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 cursor-pointer`}
          style={{ '--tw-ring-offset-color': ringOffsetColor }}
        >
          <Camera size={28} className="text-white/40" aria-hidden="true" />
        </button>
      )}

      {/* Hover overlay with change/delete buttons (visible only when foto exists and not loading) */}
      {fotoBlobUrl && (
        <div
          className={`absolute inset-0 rounded-full bg-black/55 flex items-center justify-center gap-2 transition-opacity duration-150 opacity-0 ${!isLoading ? 'group-hover:opacity-100 group-focus-within:opacity-100' : ''}`}
        >
          <button
            type="button"
            onClick={openPicker}
            disabled={isLoading}
            aria-disabled={isLoading || undefined}
            aria-label={t('cv.btn.foto_yukle')}
            className={`text-white transition-colors rounded-full p-1 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-white/80'}`}
            style={{ '--tw-ring-offset-color': ringOffsetColor }}
          >
            <Camera size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onFotoDelete}
            disabled={isLoading}
            aria-disabled={isLoading || undefined}
            aria-label={t('cv.btn.foto_kaldir')}
            className={`text-white transition-colors rounded-full p-1 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-white/80'}`}
            style={{ '--tw-ring-offset-color': ringOffsetColor }}
          >
            <Trash2 size={18} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Loading overlay — always visible during upload/delete */}
      {isLoading && (
        <div className="absolute inset-0 rounded-full bg-black/55 flex items-center justify-center">
          <Loader2 size={22} className="text-white animate-spin" aria-hidden="true" />
          <span className="sr-only">{loadingLabel}</span>
        </div>
      )}
    </div>
  );
}

// ── ClassicTemplate ───────────────────────────────────────────────────────────

/**
 * Classic Template — tek sütun, full-bleed header.
 * Faz 5: canvasEdit prop verildiğinde tüm alanlar InlineField ile düzenlenebilir.
 * canvasEdit = undefined → salt-görüntü (eski davranış korunur).
 * canvasEdit = { localEdits, onFieldSave, canvasDilDiller,
 *                onDeneyimAdd, onDeneyimDelete, onDeneyimMaddeAdd, onDeneyimMaddeDelete,
 *                onDilAdd, onDilDelete, onHasActiveEditChange }
 */
function ClassicTemplate({ cvData, selectionState, fotoBlobUrl, isDeneyimleri, t, canvasEdit }) {
  const { includedSections = {} } = selectionState || {};
  const isEdit = !!canvasEdit?.onFieldSave;

  const kisisel = cvData?.kisisel || {};
  const egitim = cvData?.egitim || {};
  const dil = cvData?.dil || {};
  const cl = canvasEdit?.localEdits || {};

  // Resolved display values — canvas field-level edits take precedence
  const displayAdSoyad  = cl.canvas_kisisel?.ad_soyad ?? kisisel.ad_soyad ?? '';
  const displayEposta   = cl.canvas_kisisel?.eposta   ?? kisisel.eposta   ?? '';
  const displayTelefon  = cl.canvas_kisisel?.telefon  ?? kisisel.telefon  ?? '';
  const displaySehir    = cl.canvas_kisisel?.sehir    ?? kisisel.sehir    ?? '';
  const displayUnvan    = cl.unvan    !== undefined ? cl.unvan    : (cvData?.unvan    || '');
  const displayHakkimda = cl.hakkimda !== undefined ? cl.hakkimda : (cvData?.hakkimda || '');
  const displayOkul            = cl.canvas_egitim?.okul             ?? egitim.okul             ?? '';
  const displayBolum           = cl.canvas_egitim?.bolum            ?? egitim.bolum            ?? '';
  const displayMezYili         = cl.canvas_egitim?.mezuniyet_yili   ?? egitim.mezuniyet_yili   ?? '';
  const displayMezDurum        = cl.canvas_egitim?.mezuniyet_durumu ?? egitim.mezuniyet_durumu ?? '';
  // Faz 7 new fields
  const displayDogumTarihi     = cl.canvas_kisisel?.dogum_tarihi    ?? kisisel.dogum_tarihi    ?? '';
  const displayInstagram       = cl.canvas_kisisel?.instagram       ?? kisisel.sosyal_medya?.instagram ?? '';
  const displayLinkedin        = cl.canvas_kisisel?.linkedin        ?? kisisel.sosyal_medya?.linkedin  ?? '';
  const displayDevamEden       = cl.canvas_egitim?.devam_eden_egitim ?? egitim.devam_eden_egitim ?? false;
  const displayYabanciDilEg    = cl.canvas_egitim?.yabanci_dil_egitimi ?? egitim.yabanci_dil_egitimi ?? '';
  const displayIngSeviye       = cl.canvas_dil?.ingilizce_seviye    ?? dil.ingilizce_seviye    ?? '';

  // dil.diller — use canvasDilDiller when in edit mode
  const activeDilDiller = isEdit ? (canvasEdit.canvasDilDiller || []) : (dil.diller || []);
  const canvasSertifikalar = isEdit ? (canvasEdit.canvasSertifikalar || []) : (egitim.sertifikalar || []);

  // displayed deneyimler with original index (needed for path-based onFieldSave)
  const displayedDenWithIdx = [];
  (isDeneyimleri || []).forEach((e, origIdx) => {
    if (selectionState?.isDeneyimleriInclude?.[origIdx] !== false) {
      displayedDenWithIdx.push({ data: e, origIdx });
    }
  });

  // displayed diller with original index
  const displayedDilWithIdx = [];
  activeDilDiller.forEach((d, origIdx) => {
    if (selectionState?.dillerInclude?.[origIdx] !== false) {
      displayedDilWithIdx.push({ data: d, origIdx });
    }
  });

  // Shared InlineField props for onEditStart/End callbacks
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};

  /**
   * renderF — returns InlineField (edit mode) or static span/null (read-only mode).
   * opts: { multiline, maxLength, ariaLabel, variant, inputCls, readCls, required, placeholder }
   */
  const renderF = (fieldId, value, opts = {}) => {
    if (!isEdit) {
      if (!value) return null;
      return <span className={opts.readCls || ''}>{value}</span>;
    }
    return (
      <InlineField
        fieldId={fieldId}
        value={value || ''}
        onSave={canvasEdit.onFieldSave}
        t={t}
        required={opts.required}
        multiline={opts.multiline}
        maxLength={opts.maxLength}
        ariaLabel={opts.ariaLabel || fieldId}
        variant={opts.variant}
        inputClassName={opts.inputCls}
        readClassName={opts.readCls}
        placeholder={opts.placeholder}
        {...editCallbacks}
      />
    );
  };

  return (
    <>
      {/* Full-bleed Header */}
      <div className="bg-gradient-to-b from-[#2E6DA4] to-[#1B3A5C] px-10 py-8 flex gap-6 items-center">
        {/* Fotoğraf */}
        <CanvasFotoCircle
          fotoBlobUrl={fotoBlobUrl}
          isEdit={isEdit}
          onFotoClick={canvasEdit?.onFotoClick}
          onFotoDelete={canvasEdit?.onFotoDelete}
          uploading={canvasEdit?.fotoUploading || false}
          deleting={canvasEdit?.fotoDeleting || false}
          t={t}
          sizeClass="w-24 h-24"
          ringOffsetColor="#2E6DA4"
          borderClass="border-white/40"
        />

        {/* Ad Soyad + Unvan + İletişim */}
        <div className="flex-1 min-w-0">
          {/* Ad Soyad */}
          <div className="leading-tight">
            {renderF('canvas.kisisel.ad_soyad', displayAdSoyad, {
              ariaLabel: t('cv_wizard.edit.field.name'),
              variant: 'header-white',
              readCls: 'text-2xl font-black text-white',
              placeholder: t('cv_wizard.canvas.placeholder_adsoyad'),
            }) || (!isEdit && <p className="text-2xl font-black text-white">{displayAdSoyad || '—'}</p>)}
          </div>
          {/* Unvan */}
          {(displayUnvan || isEdit) && (
            <div className="mt-1">
              {renderF('edit-unvan', displayUnvan, {
                ariaLabel: t('cv_wizard.edit.field.unvan'),
                variant: 'header-white',
                readCls: 'text-sm font-semibold text-[#A8D4F5]',
                placeholder: t('cv_wizard.canvas.placeholder_unvan'),
              }) || (!isEdit && <p className="text-sm font-semibold text-[#A8D4F5]">{displayUnvan}</p>)}
            </div>
          )}
          {/* İletişim bilgileri */}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#C8DEF0]">
            {(displayEposta || isEdit) && (
              <span>
                {renderF('canvas.kisisel.eposta', displayEposta, {
                  ariaLabel: t('cv.field.eposta'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_eposta'),
                }) || (!isEdit && displayEposta)}
              </span>
            )}
            {(displayTelefon || isEdit) && (
              <span>
                {renderF('canvas.kisisel.telefon', displayTelefon, {
                  ariaLabel: t('cv.field.telefon'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_telefon'),
                }) || (!isEdit && displayTelefon)}
              </span>
            )}
            {(displaySehir || isEdit) && (
              <span>
                {renderF('canvas.kisisel.sehir', displaySehir, {
                  ariaLabel: t('cv.field.sehir'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_sehir'),
                }) || (!isEdit && displaySehir)}
              </span>
            )}
            {/* Faz 7 — Doğum tarihi */}
            {(displayDogumTarihi || isEdit) && (
              <span>
                {renderF('canvas.kisisel.dogum_tarihi', displayDogumTarihi, {
                  ariaLabel: t('cv_wizard.edit.field.dogum_tarihi'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_dogum_tarihi'),
                }) || (!isEdit && displayDogumTarihi)}
              </span>
            )}
            {/* Faz 7 — Instagram */}
            {(displayInstagram || isEdit) && (
              <span>
                {renderF('canvas.kisisel.instagram', displayInstagram, {
                  ariaLabel: t('cv_wizard.edit.field.instagram'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_instagram'),
                }) || (!isEdit && displayInstagram)}
              </span>
            )}
            {/* Faz 7 — LinkedIn */}
            {(displayLinkedin || isEdit) && (
              <span>
                {renderF('canvas.kisisel.linkedin', displayLinkedin, {
                  ariaLabel: t('cv_wizard.edit.field.linkedin'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_linkedin'),
                }) || (!isEdit && displayLinkedin)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-10 py-12 md:px-14 md:py-14 space-y-7">

        {/* Hakkımda */}
        {(isEdit || includedSections.hakkimda) && (displayHakkimda || isEdit) && (
          <section data-section="hakkimda">
            <ClassicSectionHeading label={t('cv_wizard.edit.section.hakkimda')} isHidden={isEdit && !includedSections.hakkimda} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('hakkimda', v) : undefined} t={t} />
            {isEdit ? (
              <div className="text-sm text-gray-800 leading-relaxed">
                {renderF('edit-hakkimda', displayHakkimda, {
                  multiline: true,
                  maxLength: 600,
                  ariaLabel: t('cv_wizard.edit.field.hakkimda'),
                  readCls: 'text-sm text-gray-800 leading-relaxed block',
                  placeholder: t('cv_wizard.canvas.placeholder_hakkimda'),
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-800 leading-relaxed">{displayHakkimda}</p>
            )}
          </section>
        )}

        {/* İş Deneyimleri */}
        {(isEdit || includedSections.is_deneyimleri) && (displayedDenWithIdx.length > 0 || isEdit) && (
          <section data-section="is_deneyimleri">
            <ClassicSectionHeading label={t('cv_wizard.edit.section.is_deneyimi')} isHidden={isEdit && !includedSections.is_deneyimleri} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('is_deneyimleri', v) : undefined} t={t} />
            <EditableExperienceList
              displayedDenWithIdx={displayedDenWithIdx}
              canvasEdit={canvasEdit}
              t={t}
            />
          </section>
        )}

        {/* Eğitim */}
        {(isEdit || includedSections.egitim) && (displayOkul || displayBolum || isEdit) && (
          <section data-section="egitim">
            <ClassicSectionHeading label={t('cv_wizard.edit.section.egitim')} isHidden={isEdit && !includedSections.egitim} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('egitim', v) : undefined} t={t} />
            <div>
              <div className="font-bold text-gray-800 text-sm">
                {isEdit ? renderF('canvas.egitim.okul', displayOkul, {
                  ariaLabel: t('cv_wizard.edit.field.okul'),
                  readCls: 'font-bold text-gray-800 text-sm',
                  placeholder: t('cv.placeholder.okul'),
                }) : (displayOkul || null)}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 flex gap-1 flex-wrap items-center">
                {isEdit ? (
                  <>
                    {renderF('canvas.egitim.bolum', displayBolum, {
                      ariaLabel: t('cv_wizard.edit.field.bolum'),
                      readCls: 'text-xs text-gray-500',
                      placeholder: t('cv.placeholder.bolum'),
                    })}
                    {/* Faz 7 — mezuniyet_durumu */}
                    {renderF('canvas.egitim.mezuniyet_durumu', displayMezDurum, {
                      ariaLabel: t('cv_wizard.edit.field.mez_durumu'),
                      readCls: 'text-xs text-gray-500',
                      placeholder: t('cv_wizard.canvas.placeholder_mez_durumu'),
                    })}
                    {/* Faz 7 — mezuniyet_yili + devam_eden_egitim toggle */}
                    {displayDevamEden ? (
                      isEdit ? (
                        <button
                          type="button"
                          aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                          onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', false)}
                          className="text-xs text-emerald-600 border border-emerald-300 rounded px-1.5 py-0.5 hover:bg-emerald-50 transition-colors"
                        >
                          {t('cv_wizard.edit.field.devam_eden_egitim')}
                        </button>
                      ) : (
                        <span className="text-xs text-emerald-600">{t('cv_wizard.edit.field.devam_eden_egitim')}</span>
                      )
                    ) : (
                      <>
                        {renderF('canvas.egitim.mezuniyet_yili', displayMezYili ? String(displayMezYili) : '', {
                          ariaLabel: t('cv_wizard.edit.field.mezuniyet'),
                          readCls: 'text-xs text-gray-500',
                          inputCls: 'text-xs w-16',
                          placeholder: t('cv_wizard.canvas.placeholder_mez_yili'),
                        })}
                        {isEdit && (
                          <button
                            type="button"
                            aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                            onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', true)}
                            className="text-xs text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors ml-1"
                          >
                            {t('cv_wizard.edit.field.devam_eden_egitim')}?
                          </button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  displayDevamEden
                    ? [displayBolum, displayMezDurum, t('cv_wizard.edit.field.devam_eden_egitim')].filter(Boolean).join(' · ')
                    : [displayBolum, displayMezYili, displayMezDurum].filter(Boolean).join(' · ')
                )}
              </div>
              {/* Faz 7 — yabanci_dil_egitimi */}
              {(displayYabanciDilEg || isEdit) && (
                <div className="mt-1 text-xs text-gray-500">
                  {isEdit ? renderF('canvas.egitim.yabanci_dil_egitimi', displayYabanciDilEg, {
                    ariaLabel: t('cv_wizard.edit.field.yabanci_dil_egitimi'),
                    readCls: 'text-xs text-gray-500',
                    placeholder: t('cv_wizard.canvas.placeholder_yabanci_dil'),
                  }) : displayYabanciDilEg}
                </div>
              )}
              {/* Faz 7 — sertifikalar */}
              {(canvasSertifikalar.length > 0 || isEdit) && (
                <div className="mt-2">
                  <EditableSertifikalarList
                    canvasSertifikalar={canvasSertifikalar}
                    canvasEdit={canvasEdit}
                    t={t}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {/* Diller */}
        {(isEdit || includedSections.dil) && (displayedDilWithIdx.length > 0 || displayIngSeviye || dil.ingilizce_seviye || isEdit) && (
          <section data-section="dil">
            <ClassicSectionHeading label={t('cv_wizard.edit.section.dil')} isHidden={isEdit && !includedSections.dil} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('dil', v) : undefined} t={t} />
            <EditableDilList
              displayedDilWithIdx={displayedDilWithIdx}
              dilIngilizce={displayIngSeviye || dil.ingilizce_seviye}
              canvasEdit={canvasEdit}
              t={t}
            />
          </section>
        )}
      </div>
    </>
  );
}

// ── ModernSectionHeading ──────────────────────────────────────────────────────

function ModernSectionHeading({ label, isHidden, onToggle, t }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xs font-black text-gray-600 uppercase tracking-widest leading-none">{label}</span>
      {onToggle ? (
        <button
          type="button"
          onClick={() => onToggle(isHidden)}
          className="flex-shrink-0 text-gray-500 hover:text-sky-600 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sky-600 rounded transition-colors"
          title={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-label={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-pressed={!isHidden}
        >
          {isHidden ? <EyeOff size={13} aria-hidden="true" /> : <Eye size={13} aria-hidden="true" />}
        </button>
      ) : (
        isHidden && <EyeOff size={12} className="text-gray-500 shrink-0" title="CV'de gizli" aria-label="CV'de gizli" />
      )}
      <hr className="flex-1 border-t-2 border-gray-100" aria-hidden="true" />
    </div>
  );
}

// ── ModernTemplate ────────────────────────────────────────────────────────────

function ModernTemplate({ cvData, selectionState, fotoBlobUrl, isDeneyimleri, t, canvasEdit }) {
  const { includedSections = {} } = selectionState || {};
  const isEdit = !!canvasEdit?.onFieldSave;
  const kisisel = cvData?.kisisel || {};
  const egitim = cvData?.egitim || {};
  const dil = cvData?.dil || {};
  const cl = canvasEdit?.localEdits || {};
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};
  const renderF = createRenderF(isEdit, canvasEdit, t, editCallbacks);

  const displayAdSoyad  = cl.canvas_kisisel?.ad_soyad ?? kisisel.ad_soyad ?? '';
  const displayEposta   = cl.canvas_kisisel?.eposta   ?? kisisel.eposta   ?? '';
  const displayTelefon  = cl.canvas_kisisel?.telefon  ?? kisisel.telefon  ?? '';
  const displaySehir    = cl.canvas_kisisel?.sehir    ?? kisisel.sehir    ?? '';
  const displayUnvan    = cl.unvan    !== undefined ? cl.unvan    : (cvData?.unvan    || '');
  const displayHakkimda = cl.hakkimda !== undefined ? cl.hakkimda : (cvData?.hakkimda || '');
  const displayOkul          = cl.canvas_egitim?.okul             ?? egitim.okul             ?? '';
  const displayBolum         = cl.canvas_egitim?.bolum            ?? egitim.bolum            ?? '';
  const displayMezYili       = cl.canvas_egitim?.mezuniyet_yili   ?? egitim.mezuniyet_yili   ?? '';
  const displayMezDurum      = cl.canvas_egitim?.mezuniyet_durumu ?? egitim.mezuniyet_durumu ?? '';
  // Faz 7
  const displayDogumTarihi   = cl.canvas_kisisel?.dogum_tarihi    ?? kisisel.dogum_tarihi    ?? '';
  const displayInstagram     = cl.canvas_kisisel?.instagram       ?? kisisel.sosyal_medya?.instagram ?? '';
  const displayLinkedin      = cl.canvas_kisisel?.linkedin        ?? kisisel.sosyal_medya?.linkedin  ?? '';
  const displayDevamEden     = cl.canvas_egitim?.devam_eden_egitim ?? egitim.devam_eden_egitim ?? false;
  const displayYabanciDilEg  = cl.canvas_egitim?.yabanci_dil_egitimi ?? egitim.yabanci_dil_egitimi ?? '';
  const displayIngSeviye     = cl.canvas_dil?.ingilizce_seviye    ?? dil.ingilizce_seviye    ?? '';

  const activeDilDiller = isEdit ? (canvasEdit.canvasDilDiller || []) : (dil.diller || []);
  const canvasSertifikalar = isEdit ? (canvasEdit.canvasSertifikalar || []) : (egitim.sertifikalar || []);
  const displayedDenWithIdx = [];
  (isDeneyimleri || []).forEach((e, origIdx) => {
    if (selectionState?.isDeneyimleriInclude?.[origIdx] !== false) displayedDenWithIdx.push({ data: e, origIdx });
  });
  const displayedDilWithIdx = [];
  activeDilDiller.forEach((d, origIdx) => {
    if (selectionState?.dillerInclude?.[origIdx] !== false) displayedDilWithIdx.push({ data: d, origIdx });
  });

  return (
    <div className="grid grid-cols-[220px_1fr] min-h-[1123px]">
      {/* Sol lacivert panel */}
      <div className="bg-[#1B2A4A] text-white px-6 py-8 space-y-6">
        {/* Fotoğraf */}
        <div className="flex justify-center">
          <CanvasFotoCircle
            fotoBlobUrl={fotoBlobUrl}
            isEdit={isEdit}
            onFotoClick={canvasEdit?.onFotoClick}
            onFotoDelete={canvasEdit?.onFotoDelete}
            uploading={canvasEdit?.fotoUploading || false}
            deleting={canvasEdit?.fotoDeleting || false}
            t={t}
            sizeClass="w-28 h-28"
            ringOffsetColor="#1B2A4A"
            borderClass="border-white/30"
          />
        </div>
        {/* Ad Soyad */}
        <div className="text-center">
          {renderF('canvas.kisisel.ad_soyad', displayAdSoyad, {
            ariaLabel: t('cv_wizard.edit.field.name'),
            variant: 'header-white',
            readCls: 'text-lg font-black text-white',
            placeholder: t('cv_wizard.canvas.placeholder_adsoyad'),
          }) || (!isEdit && <p className="text-lg font-black text-white">{displayAdSoyad || '—'}</p>)}
        </div>
        {/* İletişim */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-black text-[#A8C4E0] uppercase tracking-widest">
            {t('cv_wizard.edit.section.kisisel')}
          </p>
          <hr className="border-[#2E4A6E]" aria-hidden="true" />
          <div className="space-y-1 text-xs text-[#C8DEF0]">
            {(displayEposta || isEdit) && (
              <div>
                {renderF('canvas.kisisel.eposta', displayEposta, {
                  ariaLabel: t('cv.field.eposta'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_eposta'),
                }) || (!isEdit && <p>{displayEposta}</p>)}
              </div>
            )}
            {(displayTelefon || isEdit) && (
              <div>
                {renderF('canvas.kisisel.telefon', displayTelefon, {
                  ariaLabel: t('cv.field.telefon'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_telefon'),
                }) || (!isEdit && <p>{displayTelefon}</p>)}
              </div>
            )}
            {(displaySehir || isEdit) && (
              <div>
                {renderF('canvas.kisisel.sehir', displaySehir, {
                  ariaLabel: t('cv.field.sehir'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_sehir'),
                }) || (!isEdit && <p>{displaySehir}</p>)}
              </div>
            )}
            {/* Faz 7 — Doğum tarihi */}
            {(displayDogumTarihi || isEdit) && (
              <div>
                {renderF('canvas.kisisel.dogum_tarihi', displayDogumTarihi, {
                  ariaLabel: t('cv_wizard.edit.field.dogum_tarihi'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_dogum_tarihi'),
                }) || (!isEdit && displayDogumTarihi && <p>{displayDogumTarihi}</p>)}
              </div>
            )}
            {/* Faz 7 — LinkedIn */}
            {(displayLinkedin || isEdit) && (
              <div>
                {renderF('canvas.kisisel.linkedin', displayLinkedin, {
                  ariaLabel: t('cv_wizard.edit.field.linkedin'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0] break-all',
                  placeholder: t('cv_wizard.canvas.placeholder_linkedin'),
                }) || (!isEdit && displayLinkedin && <p className="break-all">{displayLinkedin}</p>)}
              </div>
            )}
            {(displayInstagram || isEdit) && (
              <div>
                {renderF('canvas.kisisel.instagram', displayInstagram, {
                  ariaLabel: t('cv_wizard.edit.field.instagram'),
                  variant: 'header-white',
                  readCls: 'text-xs text-[#C8DEF0]',
                  placeholder: t('cv_wizard.canvas.placeholder_instagram'),
                }) || (!isEdit && displayInstagram && <p>{displayInstagram}</p>)}
              </div>
            )}
          </div>
        </div>
        {/* Diller */}
        {(isEdit || includedSections.dil) && (displayedDilWithIdx.length > 0 || displayIngSeviye || dil.ingilizce_seviye || isEdit) && (
          <div>
            <div className="flex items-center gap-1 mb-1">
              <p className="text-[10px] font-black text-[#A8C4E0] uppercase tracking-widest">
                {t('cv_wizard.edit.section.dil')}
              </p>
              {isEdit && canvasEdit?.onSectionToggle ? (
                <button
                  type="button"
                  onClick={() => canvasEdit.onSectionToggle('dil', !includedSections.dil)}
                  className="flex-shrink-0 text-[#6890B8] hover:text-[#A8C4E0] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sky-400 rounded transition-colors"
                  title={!includedSections.dil ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
                  aria-label={!includedSections.dil ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
                  aria-pressed={!!includedSections.dil}
                >
                  {!includedSections.dil ? <EyeOff size={10} aria-hidden="true" /> : <Eye size={10} aria-hidden="true" />}
                </button>
              ) : (
                isEdit && !includedSections.dil && <EyeOff size={10} className="text-[#6890B8] shrink-0" title="CV'de gizli" aria-label="CV'de gizli" />
              )}
            </div>
            <hr className="border-[#2E4A6E] mb-2" aria-hidden="true" />
            <EditableDilList
              displayedDilWithIdx={displayedDilWithIdx}
              dilIngilizce={displayIngSeviye || dil.ingilizce_seviye}
              canvasEdit={canvasEdit}
              t={t}
              cls={{ dilText: 'text-xs text-white', seviyeText: 'text-[#A8C4E0]' }}
            />
          </div>
        )}
      </div>
      {/* Sağ sütun */}
      <div className="px-8 py-8 space-y-6">
        {/* Unvan */}
        {(displayUnvan || isEdit) && (
          <div className="pb-3 border-b-2 border-gray-100">
            {renderF('edit-unvan', displayUnvan, {
              ariaLabel: t('cv_wizard.edit.field.unvan'),
              readCls: 'text-base font-semibold text-sky-700',
              placeholder: t('cv_wizard.canvas.placeholder_unvan'),
            }) || (!isEdit && <p className="text-base font-semibold text-sky-700">{displayUnvan}</p>)}
          </div>
        )}
        {/* Hakkımda */}
        {(isEdit || includedSections.hakkimda) && (displayHakkimda || isEdit) && (
          <section data-section="hakkimda">
            <ModernSectionHeading label={t('cv_wizard.edit.section.hakkimda')} isHidden={isEdit && !includedSections.hakkimda} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('hakkimda', v) : undefined} t={t} />
            {isEdit ? (
              <div className="text-sm text-gray-800 leading-relaxed">
                {renderF('edit-hakkimda', displayHakkimda, {
                  multiline: true, maxLength: 600,
                  ariaLabel: t('cv_wizard.edit.field.hakkimda'),
                  readCls: 'text-sm text-gray-800 leading-relaxed block',
                  placeholder: t('cv_wizard.canvas.placeholder_hakkimda'),
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-800 leading-relaxed">{displayHakkimda}</p>
            )}
          </section>
        )}
        {/* İş Deneyimleri */}
        {(isEdit || includedSections.is_deneyimleri) && (displayedDenWithIdx.length > 0 || isEdit) && (
          <section data-section="is_deneyimleri">
            <ModernSectionHeading label={t('cv_wizard.edit.section.is_deneyimi')} isHidden={isEdit && !includedSections.is_deneyimleri} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('is_deneyimleri', v) : undefined} t={t} />
            <EditableExperienceList displayedDenWithIdx={displayedDenWithIdx} canvasEdit={canvasEdit} t={t} />
          </section>
        )}
        {/* Eğitim */}
        {(isEdit || includedSections.egitim) && (displayOkul || displayBolum || isEdit) && (
          <section data-section="egitim">
            <ModernSectionHeading label={t('cv_wizard.edit.section.egitim')} isHidden={isEdit && !includedSections.egitim} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('egitim', v) : undefined} t={t} />
            <div>
              <div className="font-bold text-gray-800 text-sm">
                {isEdit ? renderF('canvas.egitim.okul', displayOkul, {
                  ariaLabel: t('cv_wizard.edit.field.okul'), readCls: 'font-bold text-gray-800 text-sm',
                  placeholder: t('cv.placeholder.okul'),
                }) : (displayOkul || null)}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 flex gap-1 flex-wrap items-center">
                {isEdit ? (
                  <>
                    {renderF('canvas.egitim.bolum', displayBolum, {
                      ariaLabel: t('cv_wizard.edit.field.bolum'), readCls: 'text-xs text-gray-500',
                      placeholder: t('cv.placeholder.bolum'),
                    })}
                    {renderF('canvas.egitim.mezuniyet_durumu', displayMezDurum, {
                      ariaLabel: t('cv_wizard.edit.field.mez_durumu'), readCls: 'text-xs text-gray-500',
                      placeholder: t('cv_wizard.canvas.placeholder_mez_durumu'),
                    })}
                    {displayDevamEden ? (
                      <button
                        type="button"
                        aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                        onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', false)}
                        className="text-xs text-emerald-600 border border-emerald-300 rounded px-1.5 py-0.5 hover:bg-emerald-50 transition-colors"
                      >
                        {t('cv_wizard.edit.field.devam_eden_egitim')}
                      </button>
                    ) : (
                      <>
                        {renderF('canvas.egitim.mezuniyet_yili', displayMezYili ? String(displayMezYili) : '', {
                          ariaLabel: t('cv_wizard.edit.field.mezuniyet'), readCls: 'text-xs text-gray-500',
                          inputCls: 'text-xs w-16', placeholder: t('cv_wizard.canvas.placeholder_mez_yili'),
                        })}
                        {isEdit && (
                          <button
                            type="button"
                            aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                            onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', true)}
                            className="text-xs text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors ml-1"
                          >
                            {t('cv_wizard.edit.field.devam_eden_egitim')}?
                          </button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  displayDevamEden
                    ? [displayBolum, displayMezDurum, t('cv_wizard.edit.field.devam_eden_egitim')].filter(Boolean).join(' · ')
                    : [displayBolum, displayMezYili, displayMezDurum].filter(Boolean).join(' · ')
                )}
              </div>
              {(displayYabanciDilEg || isEdit) && (
                <div className="mt-1 text-xs text-gray-500">
                  {isEdit ? renderF('canvas.egitim.yabanci_dil_egitimi', displayYabanciDilEg, {
                    ariaLabel: t('cv_wizard.edit.field.yabanci_dil_egitimi'),
                    readCls: 'text-xs text-gray-500',
                    placeholder: t('cv_wizard.canvas.placeholder_yabanci_dil'),
                  }) : displayYabanciDilEg}
                </div>
              )}
              {(canvasSertifikalar.length > 0 || isEdit) && (
                <div className="mt-2">
                  <EditableSertifikalarList canvasSertifikalar={canvasSertifikalar} canvasEdit={canvasEdit} t={t} />
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ── MinimalTemplate ───────────────────────────────────────────────────────────

function MinimalTemplate({ cvData, selectionState, fotoBlobUrl: _foto, isDeneyimleri, t, canvasEdit }) {
  const { includedSections = {} } = selectionState || {};
  const isEdit = !!canvasEdit?.onFieldSave;
  const kisisel = cvData?.kisisel || {};
  const egitim = cvData?.egitim || {};
  const dil = cvData?.dil || {};
  const cl = canvasEdit?.localEdits || {};
  const editCallbacks = isEdit ? {
    onEditStart: canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(true) : undefined,
    onEditEnd:   canvasEdit.onHasActiveEditChange ? () => canvasEdit.onHasActiveEditChange(false) : undefined,
  } : {};
  const renderF = createRenderF(isEdit, canvasEdit, t, editCallbacks);

  const displayAdSoyad  = cl.canvas_kisisel?.ad_soyad ?? kisisel.ad_soyad ?? '';
  const displayEposta   = cl.canvas_kisisel?.eposta   ?? kisisel.eposta   ?? '';
  const displayTelefon  = cl.canvas_kisisel?.telefon  ?? kisisel.telefon  ?? '';
  const displaySehir    = cl.canvas_kisisel?.sehir    ?? kisisel.sehir    ?? '';
  const displayUnvan    = cl.unvan    !== undefined ? cl.unvan    : (cvData?.unvan    || '');
  const displayHakkimda = cl.hakkimda !== undefined ? cl.hakkimda : (cvData?.hakkimda || '');
  const displayOkul          = cl.canvas_egitim?.okul             ?? egitim.okul             ?? '';
  const displayBolum         = cl.canvas_egitim?.bolum            ?? egitim.bolum            ?? '';
  const displayMezYili       = cl.canvas_egitim?.mezuniyet_yili   ?? egitim.mezuniyet_yili   ?? '';
  const displayMezDurum      = cl.canvas_egitim?.mezuniyet_durumu ?? egitim.mezuniyet_durumu ?? '';
  // Faz 7
  const displayDogumTarihi   = cl.canvas_kisisel?.dogum_tarihi    ?? kisisel.dogum_tarihi    ?? '';
  const displayInstagram     = cl.canvas_kisisel?.instagram       ?? kisisel.sosyal_medya?.instagram ?? '';
  const displayLinkedin      = cl.canvas_kisisel?.linkedin        ?? kisisel.sosyal_medya?.linkedin  ?? '';
  const displayDevamEden     = cl.canvas_egitim?.devam_eden_egitim ?? egitim.devam_eden_egitim ?? false;
  const displayYabanciDilEg  = cl.canvas_egitim?.yabanci_dil_egitimi ?? egitim.yabanci_dil_egitimi ?? '';
  const displayIngSeviye     = cl.canvas_dil?.ingilizce_seviye    ?? dil.ingilizce_seviye    ?? '';

  const activeDilDiller = isEdit ? (canvasEdit.canvasDilDiller || []) : (dil.diller || []);
  const canvasSertifikalar = isEdit ? (canvasEdit.canvasSertifikalar || []) : (egitim.sertifikalar || []);
  const displayedDenWithIdx = [];
  (isDeneyimleri || []).forEach((e, origIdx) => {
    if (selectionState?.isDeneyimleriInclude?.[origIdx] !== false) displayedDenWithIdx.push({ data: e, origIdx });
  });
  const displayedDilWithIdx = [];
  activeDilDiller.forEach((d, origIdx) => {
    if (selectionState?.dillerInclude?.[origIdx] !== false) displayedDilWithIdx.push({ data: d, origIdx });
  });

  const MinSection = ({ label, isHidden, onToggle }) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[11px] font-black text-gray-600 uppercase tracking-widest">{label}</span>
      {onToggle ? (
        <button
          type="button"
          onClick={() => onToggle(isHidden)}
          className="flex-shrink-0 text-gray-500 hover:text-sky-600 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sky-600 rounded transition-colors"
          title={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-label={isHidden ? t('cv_wizard.edit.section_toggle_show') : t('cv_wizard.edit.section_toggle_hide')}
          aria-pressed={!isHidden}
        >
          {isHidden ? <EyeOff size={13} aria-hidden="true" /> : <Eye size={13} aria-hidden="true" />}
        </button>
      ) : (
        isHidden && <EyeOff size={12} className="text-gray-500 shrink-0" title="CV'de gizli" aria-label="CV'de gizli" />
      )}
    </div>
  );

  return (
    <div className="px-10 py-10 space-y-6 min-h-[1123px]">
      {/* Header */}
      <div className="pb-4 border-b border-gray-200">
        <div className="leading-tight">
          {renderF('canvas.kisisel.ad_soyad', displayAdSoyad, {
            ariaLabel: t('cv_wizard.edit.field.name'),
            readCls: 'text-3xl font-black text-gray-900',
            placeholder: t('cv_wizard.canvas.placeholder_adsoyad'),
          }) || (!isEdit && <p className="text-3xl font-black text-gray-900">{displayAdSoyad || '—'}</p>)}
        </div>
        {(displayUnvan || isEdit) && (
          <div className="mt-1">
            {renderF('edit-unvan', displayUnvan, {
              ariaLabel: t('cv_wizard.edit.field.unvan'),
              readCls: 'text-base text-gray-500',
              placeholder: t('cv_wizard.canvas.placeholder_unvan'),
            }) || (!isEdit && <p className="text-base text-gray-500">{displayUnvan}</p>)}
          </div>
        )}
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
          {(displayEposta || isEdit) && (
            <span>
              {renderF('canvas.kisisel.eposta', displayEposta, {
                ariaLabel: t('cv.field.eposta'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_eposta'),
              }) || (!isEdit && displayEposta)}
            </span>
          )}
          {(displayTelefon || isEdit) && (
            <span>
              {renderF('canvas.kisisel.telefon', displayTelefon, {
                ariaLabel: t('cv.field.telefon'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_telefon'),
              }) || (!isEdit && displayTelefon)}
            </span>
          )}
          {(displaySehir || isEdit) && (
            <span>
              {renderF('canvas.kisisel.sehir', displaySehir, {
                ariaLabel: t('cv.field.sehir'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_sehir'),
              }) || (!isEdit && displaySehir)}
            </span>
          )}
          {/* Faz 7 — Doğum tarihi */}
          {(displayDogumTarihi || isEdit) && (
            <span>
              {renderF('canvas.kisisel.dogum_tarihi', displayDogumTarihi, {
                ariaLabel: t('cv_wizard.edit.field.dogum_tarihi'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_dogum_tarihi'),
              }) || (!isEdit && displayDogumTarihi)}
            </span>
          )}
          {/* Faz 7 — Instagram */}
          {(displayInstagram || isEdit) && (
            <span>
              {renderF('canvas.kisisel.instagram', displayInstagram, {
                ariaLabel: t('cv_wizard.edit.field.instagram'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_instagram'),
              }) || (!isEdit && displayInstagram)}
            </span>
          )}
          {/* Faz 7 — LinkedIn */}
          {(displayLinkedin || isEdit) && (
            <span>
              {renderF('canvas.kisisel.linkedin', displayLinkedin, {
                ariaLabel: t('cv_wizard.edit.field.linkedin'), readCls: 'text-xs text-gray-500',
                placeholder: t('cv_wizard.canvas.placeholder_linkedin'),
              }) || (!isEdit && displayLinkedin)}
            </span>
          )}
        </div>
      </div>

      {/* Hakkımda */}
      {(isEdit || includedSections.hakkimda) && (displayHakkimda || isEdit) && (
        <section data-section="hakkimda">
          <MinSection label={t('cv_wizard.edit.section.hakkimda')} isHidden={isEdit && !includedSections.hakkimda} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('hakkimda', v) : undefined} />
          <hr className="border-[#CCCCCC] mb-3" aria-hidden="true" />
          {isEdit ? (
            <div className="text-sm text-gray-700 leading-relaxed">
              {renderF('edit-hakkimda', displayHakkimda, {
                multiline: true, maxLength: 600,
                ariaLabel: t('cv_wizard.edit.field.hakkimda'),
                readCls: 'text-sm text-gray-700 leading-relaxed block',
                placeholder: t('cv_wizard.canvas.placeholder_hakkimda'),
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed">{displayHakkimda}</p>
          )}
        </section>
      )}

      {/* İş Deneyimleri */}
      {(isEdit || includedSections.is_deneyimleri) && (displayedDenWithIdx.length > 0 || isEdit) && (
        <section data-section="is_deneyimleri">
          <MinSection label={t('cv_wizard.edit.section.is_deneyimi')} isHidden={isEdit && !includedSections.is_deneyimleri} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('is_deneyimleri', v) : undefined} />
          <hr className="border-[#CCCCCC] mb-3" aria-hidden="true" />
          <EditableExperienceList displayedDenWithIdx={displayedDenWithIdx} canvasEdit={canvasEdit} t={t} />
        </section>
      )}

      {/* Eğitim */}
      {(isEdit || includedSections.egitim) && (displayOkul || displayBolum || isEdit) && (
        <section data-section="egitim">
          <MinSection label={t('cv_wizard.edit.section.egitim')} isHidden={isEdit && !includedSections.egitim} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('egitim', v) : undefined} />
          <hr className="border-[#CCCCCC] mb-3" aria-hidden="true" />
          <div>
            <div className="font-bold text-gray-800 text-sm">
              {isEdit ? renderF('canvas.egitim.okul', displayOkul, {
                ariaLabel: t('cv_wizard.edit.field.okul'), readCls: 'font-bold text-gray-800 text-sm',
                placeholder: t('cv.placeholder.okul'),
              }) : (displayOkul || null)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 flex gap-1 flex-wrap items-center">
              {isEdit ? (
                <>
                  {renderF('canvas.egitim.bolum', displayBolum, {
                    ariaLabel: t('cv_wizard.edit.field.bolum'), readCls: 'text-xs text-gray-500',
                    placeholder: t('cv.placeholder.bolum'),
                  })}
                  {renderF('canvas.egitim.mezuniyet_durumu', displayMezDurum, {
                    ariaLabel: t('cv_wizard.edit.field.mez_durumu'), readCls: 'text-xs text-gray-500',
                    placeholder: t('cv_wizard.canvas.placeholder_mez_durumu'),
                  })}
                  {displayDevamEden ? (
                    <button
                      type="button"
                      aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                      onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', false)}
                      className="text-xs text-emerald-600 border border-emerald-300 rounded px-1.5 py-0.5 hover:bg-emerald-50 transition-colors"
                    >
                      {t('cv_wizard.edit.field.devam_eden_egitim')}
                    </button>
                  ) : (
                    <>
                      {renderF('canvas.egitim.mezuniyet_yili', displayMezYili ? String(displayMezYili) : '', {
                        ariaLabel: t('cv_wizard.edit.field.mezuniyet'), readCls: 'text-xs text-gray-500',
                        inputCls: 'text-xs w-16', placeholder: t('cv_wizard.canvas.placeholder_mez_yili'),
                      })}
                      {isEdit && (
                        <button
                          type="button"
                          aria-label={t('cv_wizard.edit.field.devam_eden_egitim')}
                          onClick={() => canvasEdit.onFieldSave('canvas.egitim.devam_eden_egitim', true)}
                          className="text-xs text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors ml-1"
                        >
                          {t('cv_wizard.edit.field.devam_eden_egitim')}?
                        </button>
                      )}
                    </>
                  )}
                </>
              ) : (
                displayDevamEden
                  ? [displayBolum, displayMezDurum, t('cv_wizard.edit.field.devam_eden_egitim')].filter(Boolean).join(' · ')
                  : [displayBolum, displayMezYili, displayMezDurum].filter(Boolean).join(' · ')
              )}
            </div>
            {(displayYabanciDilEg || isEdit) && (
              <div className="mt-1 text-xs text-gray-500">
                {isEdit ? renderF('canvas.egitim.yabanci_dil_egitimi', displayYabanciDilEg, {
                  ariaLabel: t('cv_wizard.edit.field.yabanci_dil_egitimi'),
                  readCls: 'text-xs text-gray-500',
                  placeholder: t('cv_wizard.canvas.placeholder_yabanci_dil'),
                }) : displayYabanciDilEg}
              </div>
            )}
            {(canvasSertifikalar.length > 0 || isEdit) && (
              <div className="mt-2">
                <EditableSertifikalarList canvasSertifikalar={canvasSertifikalar} canvasEdit={canvasEdit} t={t} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Diller */}
      {(isEdit || includedSections.dil) && (displayedDilWithIdx.length > 0 || displayIngSeviye || dil.ingilizce_seviye || isEdit) && (
        <section data-section="dil">
          <MinSection label={t('cv_wizard.edit.section.dil')} isHidden={isEdit && !includedSections.dil} onToggle={isEdit && canvasEdit?.onSectionToggle ? (v) => canvasEdit.onSectionToggle('dil', v) : undefined} />
          <hr className="border-[#CCCCCC] mb-3" aria-hidden="true" />
          <EditableDilList
            displayedDilWithIdx={displayedDilWithIdx}
            dilIngilizce={displayIngSeviye || dil.ingilizce_seviye}
            canvasEdit={canvasEdit}
            t={t}
          />
        </section>
      )}
    </div>
  );
}

// ── CVCanvas (named export) ───────────────────────────────────────────────────

/**
 * CVCanvas — A4 kağıt sarmalayıcı (794 × min-1123 px).
 * templateId prop ile Classic/Modern/Minimal arasında geçiş.
 * canvasEdit = undefined → salt-görüntü (read-only render, no InlineField).
 */
export function CVCanvas({ cvData, selectionState, fotoBlobUrl, isDeneyimleri, t, canvasEdit, templateId }) {
  const TemplateComponent =
    templateId === 'modern'  ? ModernTemplate  :
    templateId === 'minimal' ? MinimalTemplate :
    ClassicTemplate;

  return (
    <div
      className="flex justify-center items-start bg-slate-100 py-8 px-4 md:px-8 rounded-xl"
      aria-label={t('cv_wizard.canvas.aria_label')}
    >
      <div className="w-full max-w-[794px] bg-white rounded-sm shadow-[0_4px_32px_rgba(0,0,0,0.14)] min-h-[1123px] overflow-hidden">
        <TemplateComponent
          cvData={cvData}
          selectionState={selectionState}
          fotoBlobUrl={fotoBlobUrl}
          isDeneyimleri={isDeneyimleri}
          t={t}
          canvasEdit={canvasEdit}
        />
      </div>
    </div>
  );
}

// ── TEMPLATE_IDS (named export) ───────────────────────────────────────────────

/** Backend ALLOWED_TEMPLATES ile tutarlı — tek kaynak. */
export const TEMPLATE_IDS = ['classic', 'modern', 'minimal'];
